import { cloneWorkflow as clone } from 'api/WorkflowsAPI';
import { getJourneyStudioUrl } from 'utils/appRoutes';
import type { AppDispatch } from 'modules/app/components/App';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { NODE_TYPES } from 'modules/workflows/constants';
import { WorkflowType } from 'modules/workflows/enums';
import { CallWebhookNodeData, Node } from 'modules/workflows/types';
import miscUtils from 'modules/workflows/utils/nodeUtils/miscUtils';
import { TrimmedWorkflow } from './CloneJourneyModal.types';
import { ERROR_MESSAGE } from './constants';
import { CloneFormSchemaType } from './context/CloneJourneyFormProvider';
import { Campaign, CloningCampaignData } from './context/CloningCampaignsContext.types';

export const cloneJourney = async (
  workflow: TrimmedWorkflow,
  dispatch: AppDispatch,
  workflowName = '',
) => {
  try {
    const cloneParams: {
      workflowId: number;
      workflowType?: WorkflowType;
      name: string;
    } = {
      workflowId: workflow.id,
      workflowType: workflow.workflowType,
      name: workflowName,
    };
    // return tuple looks like [JOURNEY, LIVE, DRAFT]
    const [, publishedId, draftId] = await clone(cloneParams);
    const id = workflow.workflowType === WorkflowType.DRAFT ? draftId : publishedId;
    dispatch(
      addPositiveToast(
        <>
          <strong>{workflowName}</strong> created!
        </>,
        {
          actions: [
            {
              title: 'Go to journey',
              href: getJourneyStudioUrl(id, workflow.workflowType as WorkflowType),
              externalAction: true,
            },
          ],
        },
      ),
    );
  } catch {
    dispatch(addNegativeToastWithErrorId(ERROR_MESSAGE, 'cloneJourney'));
  }
};

export const getWorkflowContentToCopy = (nodes: Node[] = [], campaignNodes: Campaign[] = []) => {
  const hasWebhooks = nodes.some(
    (node) =>
      node.type === NODE_TYPES.CALL_WEBHOOK &&
      miscUtils.webhook.isSetup(node.nodeData as CallWebhookNodeData),
  );

  const hasCampaigns = campaignNodes.length > 0;

  const hasContentToCopy = hasWebhooks || hasCampaigns;

  return { hasWebhooks, hasCampaigns, hasContentToCopy };
};

export const getCampaignValues = (
  campaignValues: CloneFormSchemaType = {},
): CloningCampaignData[] =>
  Object.entries(campaignValues).map(([key, val]) => ({
    campaignId: Number(key),
    ...('messageTypeIdEmail' in val && {
      messageTypeId: val.messageTypeIdEmail,
      senderEmail: {
        email: val.senderEmail,
      },
    }),
    ...('messageTypeIdSMS' in val && {
      messageTypeId: val.messageTypeIdSMS,
      senderSms: { id: val.senderSMS },
    }),
    ...('messageTypeId' in val && {
      messageTypeId: val.messageTypeId,
    }),
  }));
